import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface KpiCardProps {
  label: string;
  value: number | null | undefined;
  icon: IconDefinition;
  iconCls?: string;
  isFuture?: boolean;
  format?: 'number' | 'minutes' | 'days';
  /** When set, the card becomes clickable and navigates here (e.g. a filtered queue). */
  to?: string;
}

function formatValue(value: number | null | undefined, format: KpiCardProps['format']): string {
  const n = value ?? 0;
  if (format === 'minutes') {
    if (n === 0) return '—';
    if (n < 60) return `${n}m`;
    return `${Math.floor(n / 60)}h ${n % 60}m`;
  }
  if (format === 'days') {
    if (n === 0) return '—';
    return `${n}d`;
  }
  return n.toLocaleString();
}

export function KpiCard({
  label,
  value,
  icon,
  iconCls = 'bg-primary/10 text-primary',
  isFuture = false,
  format = 'number',
  to,
}: KpiCardProps) {
  const navigate = useNavigate();
  const clickable = !isFuture && to !== undefined;

  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => navigate(to) : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(to);
              }
            }
          : undefined
      }
      className={`relative rounded-xl border border-border bg-card p-5 ${
        clickable ? 'cursor-pointer transition-colors hover:bg-muted/30' : ''
      }`}
    >
      {isFuture && (
        <span className="absolute right-3 top-3 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
          coming soon
        </span>
      )}
      <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${iconCls}`}>
        <FontAwesomeIcon icon={icon} className="h-4 w-4" />
      </div>
      <p
        className={`mt-3 text-3xl font-bold tracking-tight ${isFuture ? 'text-muted-foreground' : 'text-foreground'}`}
      >
        {isFuture ? '—' : formatValue(value, format)}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

export function KpiCardSkeleton() {
  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-3">
      <div className="h-9 w-9 rounded-lg skeleton-shimmer" />
      <div className="h-8 w-20 rounded skeleton-shimmer" />
      <div className="h-4 w-32 rounded skeleton-shimmer" />
    </div>
  );
}
